/**
 * Avatar-Auswahl als zentrierter Overlay-Dialog.
 * Zeigt vorgefertigte Portrait-Bilder + "Eigenes Bild — Coming Soon".
 */
import { fontSm, fontXs, gapMd, gapSm, padMd, padSm, radius } from './UITheme';

const BRD = 'rgba(100,180,255,0.2)';
const ACCENT = 'rgba(100,180,255,0.7)';
const MUTED = 'rgba(100,180,255,0.35)';
const SEL_BRD = '#8cf';
const UNSEL_BRD = 'rgba(100,180,255,0.15)';

export interface AvatarPreset { url: string; label: string; }

export const AVATAR_PRESETS: AvatarPreset[] = [
  { url: '/assets/portraits/male_commander_1.png', label: 'Commander M1' },
  { url: '/assets/portraits/male_commander_2.png', label: 'Commander M2' },
  { url: '/assets/portraits/male_commander_3.png', label: 'Commander M3' },
  { url: '/assets/portraits/female_commander_1.png', label: 'Commander F1' },
  { url: '/assets/portraits/female_commander_2.png', label: 'Commander F2' },
  { url: '/assets/portraits/female_commander_3.png', label: 'Commander F3' },
];

export function renderAvatarImage(id: number, size: string): HTMLDivElement {
  const preset = AVATAR_PRESETS[id] ?? AVATAR_PRESETS[0];
  const el = document.createElement('div');
  Object.assign(el.style, {
    width: size, height: size, minWidth: size, borderRadius: '50%',
    border: '2px solid rgba(100,180,255,0.3)', overflow: 'hidden',
    background: 'rgba(0,0,0,0.4)',
  });
  const img = document.createElement('img');
  img.src = preset.url; img.alt = preset.label;
  Object.assign(img.style, { width: '100%', height: '100%', objectFit: 'cover', display: 'block' });
  el.appendChild(img);
  return el;
}

export class AccountAvatarGrid {
  readonly el: HTMLDivElement;

  constructor(selectedId: number, onSelect: (id: number) => void, onClose: () => void) {
    this.el = document.createElement('div');
    Object.assign(this.el.style, {
      position: 'fixed', inset: '0', zIndex: '60000',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'rgba(0,0,0,0.7)', fontFamily: 'monospace',
    });
    this.el.addEventListener('click', () => onClose());

    const card = document.createElement('div');
    Object.assign(card.style, {
      background: 'linear-gradient(180deg,rgba(12,28,60,0.97),rgba(6,16,38,0.97))',
      border: `1px solid ${BRD}`, borderRadius: radius(), padding: padMd(),
      display: 'flex', flexDirection: 'column', gap: gapSm(),
      boxShadow: '0 8px 32px rgba(0,0,0,0.6)',
      width: 'clamp(220px, 65vw, 300px)',
    });
    card.addEventListener('click', (e) => e.stopPropagation());

    const title = Object.assign(document.createElement('div'), { textContent: 'Avatar wählen' });
    Object.assign(title.style, {
      fontSize: fontSm(), color: ACCENT, textTransform: 'uppercase',
      letterSpacing: '1px', textAlign: 'center',
    });

    const grid = document.createElement('div');
    Object.assign(grid.style, {
      display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)',
      gap: gapMd(), justifyItems: 'center',
    });

    const cellSize = 'clamp(52px, 14vw, 70px)';
    for (let i = 0; i < AVATAR_PRESETS.length; i++) {
      const cell = document.createElement('div');
      Object.assign(cell.style, {
        width: cellSize, height: cellSize, borderRadius: '50%', cursor: 'pointer',
        border: i === selectedId ? `2.5px solid ${SEL_BRD}` : `2px solid ${UNSEL_BRD}`,
        overflow: 'hidden', transition: 'border-color 0.15s, transform 0.15s',
        boxShadow: i === selectedId ? `0 0 8px ${SEL_BRD}` : 'none',
      });
      const img = document.createElement('img');
      img.src = AVATAR_PRESETS[i].url;
      Object.assign(img.style, { width: '100%', height: '100%', objectFit: 'cover', display: 'block' });
      cell.appendChild(img);
      cell.addEventListener('pointerenter', () => { cell.style.transform = 'scale(1.08)'; });
      cell.addEventListener('pointerleave', () => { cell.style.transform = 'scale(1)'; });
      cell.addEventListener('click', () => onSelect(i));
      grid.appendChild(cell);
    }

    const comingSoon = document.createElement('div');
    Object.assign(comingSoon.style, {
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      gap: '6px', padding: padSm(), borderRadius: radius(),
      border: `1px dashed ${MUTED}`, cursor: 'default',
    });
    const camIcon = Object.assign(document.createElement('span'), { textContent: '📷' });
    camIcon.style.fontSize = fontXs();
    const csLabel = Object.assign(document.createElement('span'), { textContent: 'Eigenes Bild — Coming Soon' });
    Object.assign(csLabel.style, { fontSize: fontXs(), color: MUTED, fontStyle: 'italic' });
    comingSoon.append(camIcon, csLabel);

    card.append(title, grid, comingSoon);
    this.el.appendChild(card);
  }

  remove(): void { this.el.remove(); }
}
